/**
 * Phase 4 — coach-side per-player tactical briefs.
 *
 * Slide-up panel anchored above the tactics HUD. Lists the 11 home
 * (or away) briefs as collapsible cards; each card shows the slot, the
 * named role, and the 4 brief sections (in possession / out of
 * possession / transitions / set pieces).
 *
 * Header chips:
 *   - Regenerate — runs the rule engine and writes the result back.
 *   - Print — placeholder, lit by Phase 7 (player roster route).
 *
 * Per-card edit pencil opens textareas for each section; saving an
 * override flips that brief's `source` to `'manual'` and persists it.
 *
 * Stale badge: when `layoutStale` is true, a HudHint strip across the
 * top prompts the coach to regenerate (the underlying input has
 * changed since the briefs were last generated).
 */

import { useEffect, useMemo, useState } from 'react';
import type { PlayerBrief } from '@4cm/core/tactics';
import { HudChip } from './HudChip';
import { HudHint } from './HudHint';
import { rosterIdentityFor } from '../../lib/roster';
import './PlayerBriefsDrawer.css';

export interface PlayerBriefsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Briefs by playerKey; missing/empty briefs render a "not generated yet" placeholder. */
  briefs: Record<string, PlayerBrief>;
  /** Spots to drive the card list — one card per home spot in slot order. */
  spots: { role: string }[];
  /** Side this drawer is for. Used to compose the playerKey ("home-3" / "away-7"). */
  side: 'home' | 'away';
  /** True when the layout's brief-affecting inputs changed since the last regen. */
  layoutStale: boolean;
  /** Fired when the coach taps "Regenerate" or has no briefs yet and taps "Generate".
   *  Receives the current state of the AI-polish toggle so the caller can decide
   *  whether to run the Claude pass after the rule engine. */
  onRegenerate: (options: { polish: boolean }) => void;
  /** Persist an edited brief — caller flips its source to 'manual' and saves. */
  onEdit: (playerKey: string, next: PlayerBrief) => void;
  /** Disables interaction during an in-flight polish call. Optional. */
  busy?: boolean;
  /** Per-player edit history. Newest first, capped at 5 by the engine. */
  history?: Record<string, PlayerBrief[]>;
  /** Coach restores an older brief from history. */
  onRestore?: (playerKey: string, version: PlayerBrief) => void;
  /** Optional href to the print-friendly roster page. When provided the
   *  drawer header renders a "Print page" chip linking out (new tab). */
  rosterHref?: string;
  /** Coach-supplied roster identities (name + jersey number) keyed by
   *  playerKey. Cards render initials / numbers from this. */
  roster?: Record<string, { name?: string; number?: number }>;
  /** Persist a roster edit. Caller writes to layout.roster. */
  onRosterEdit?: (playerKey: string, next: { name?: string; number?: number }) => void;
  /** Import the team's existing players (from the Players page) into
   *  the roster. Fills name slots in index order — coach re-arranges
   *  after. Caller resolves the players list (loadLocalPlayers in v1)
   *  + writes through onRosterEdit per spot. Header renders an
   *  "Import" chip when provided. */
  onImportRoster?: () => void;
}

function formatHistoryTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    // Show "May 14, 10:32" — short month + day + 24h time.
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const SECTIONS: Array<{ key: keyof Pick<PlayerBrief, 'inPossession' | 'outOfPossession' | 'transitions' | 'setPieces'>; label: string }> = [
  { key: 'inPossession', label: 'In possession' },
  { key: 'outOfPossession', label: 'Out of possession' },
  { key: 'transitions', label: 'Transitions' },
  { key: 'setPieces', label: 'Set pieces' },
];

export function PlayerBriefsDrawer({
  open,
  onClose,
  briefs,
  spots,
  side,
  layoutStale,
  onRegenerate,
  onEdit,
  busy = false,
  history = {},
  onRestore,
  rosterHref,
  roster = {},
  onRosterEdit,
  onImportRoster,
}: PlayerBriefsDrawerProps) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [historyKey, setHistoryKey] = useState<string | null>(null);
  // AI polish toggle. Off by default — the rule engine produces a
  // valid brief without ever calling Claude. Coach opts in per regen.
  const [aiPolish, setAiPolish] = useState(false);

  // Reset transient UI state when the drawer closes so a re-open
  // starts clean.
  useEffect(() => {
    if (!open) {
      setExpandedKey(null);
      setEditingKey(null);
    }
  }, [open]);

  // Escape closes — registered only while open so we don't intercept
  // global keystrokes on idle pages.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const briefList = useMemo(() => spots.map((spot, idx) => ({
    playerKey: `${side}-${idx}`,
    slot: spot.role,
    brief: briefs[`${side}-${idx}`] ?? null,
  })), [briefs, spots, side]);

  const hasAnyBriefs = useMemo(
    () => Object.keys(briefs).length > 0,
    [briefs],
  );

  if (!open) return null;

  return (
    <div
      className="player-briefs-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="Player briefs"
      data-testid="player-briefs-drawer"
    >
      <div className="player-briefs-drawer__header">
        <h2 className="player-briefs-drawer__title">Player briefs</h2>
        <div className="player-briefs-drawer__header-chips">
          <HudChip
            active={aiPolish}
            ariaLabel={
              aiPolish ? 'AI polish on — disable' : 'AI polish off — enable'
            }
            onClick={() => setAiPolish((on) => !on)}
          >
            AI polish
          </HudChip>
          <HudChip
            active={false}
            ariaLabel={
              busy
                ? 'Regenerating…'
                : hasAnyBriefs
                ? 'Regenerate player briefs'
                : 'Generate player briefs'
            }
            onClick={() => {
              if (busy) return;
              onRegenerate({ polish: aiPolish });
            }}
          >
            {busy ? 'Working…' : hasAnyBriefs ? 'Regenerate' : 'Generate'}
          </HudChip>
          {onImportRoster && (
            <HudChip
              active={false}
              ariaLabel="Import names from the team's existing roster"
              onClick={() => onImportRoster()}
            >
              Import roster
            </HudChip>
          )}
          {rosterHref && hasAnyBriefs && (
            <a
              className="player-briefs-drawer__roster-link"
              href={rosterHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open print-friendly roster page"
            >
              Print page ↗
            </a>
          )}
        </div>
        <button
          type="button"
          className="player-briefs-drawer__close"
          onClick={onClose}
          aria-label="Close player briefs"
        >
          ×
        </button>
      </div>

      {layoutStale && hasAnyBriefs && (
        <HudHint>
          Layout changed — regenerate to refresh briefs.
        </HudHint>
      )}

      <div className="player-briefs-drawer__list" role="list">
        {!hasAnyBriefs && (
          <div className="player-briefs-drawer__empty">
            No briefs yet. Tap <strong>Generate</strong> to produce a tactical brief for every player based on the current layout.
          </div>
        )}
        {hasAnyBriefs && briefList.map(({ playerKey, slot, brief }, idx) => {
          const expanded = expandedKey === playerKey;
          const editing = editingKey === playerKey;
          const rosterEntry = roster[playerKey];
          const identity = rosterIdentityFor(idx, rosterEntry);
          return (
            <div
              key={playerKey}
              className="player-briefs-drawer__row"
              role="listitem"
              data-testid={`brief-card-${playerKey}`}
            >
              <div className="player-briefs-drawer__row-header">
                <button
                  type="button"
                  className="player-briefs-drawer__row-summary"
                  onClick={() => setExpandedKey(expanded ? null : playerKey)}
                  aria-expanded={expanded}
                  aria-controls={`brief-body-${playerKey}`}
                >
                  <span className="player-briefs-drawer__row-slot">{slot}</span>
                  <span className="player-briefs-drawer__row-key">
                    {identity.card}
                  </span>
                  {brief?.namedRole && (
                    <span className="player-briefs-drawer__row-role">{brief.namedRole}</span>
                  )}
                  {brief?.source === 'manual' && (
                    <span className="player-briefs-drawer__row-source" data-source="manual">
                      edited
                    </span>
                  )}
                  {brief?.source === 'ai' && (
                    <span className="player-briefs-drawer__row-source" data-source="ai">
                      AI
                    </span>
                  )}
                </button>
              </div>
              {expanded && onRosterEdit && (
                <div className="player-briefs-drawer__row-roster">
                  <label className="player-briefs-drawer__row-roster-field">
                    <span className="player-briefs-drawer__row-roster-label">
                      Player name
                    </span>
                    <input
                      type="text"
                      className="player-briefs-drawer__row-roster-input"
                      placeholder="e.g. Mohamed Salah"
                      defaultValue={rosterEntry?.name ?? ''}
                      onBlur={(e) => {
                        const nextName = e.target.value.trim();
                        const nextEntry = {
                          number: rosterEntry?.number,
                          name: nextName || undefined,
                        };
                        onRosterEdit(playerKey, nextEntry);
                      }}
                      data-testid={`brief-roster-name-${playerKey}`}
                    />
                  </label>
                  <label className="player-briefs-drawer__row-roster-field">
                    <span className="player-briefs-drawer__row-roster-label">
                      Number
                    </span>
                    <input
                      type="number"
                      min={1}
                      max={99}
                      className="player-briefs-drawer__row-roster-input player-briefs-drawer__row-roster-input--small"
                      placeholder="11"
                      defaultValue={
                        typeof rosterEntry?.number === 'number'
                          ? rosterEntry.number
                          : ''
                      }
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        const parsed = raw === '' ? undefined : Number(raw);
                        const nextEntry = {
                          name: rosterEntry?.name,
                          number:
                            typeof parsed === 'number' && Number.isFinite(parsed)
                              ? parsed
                              : undefined,
                        };
                        onRosterEdit(playerKey, nextEntry);
                      }}
                      data-testid={`brief-roster-number-${playerKey}`}
                    />
                  </label>
                </div>
              )}
              {expanded && (
                <div className="player-briefs-drawer__row-body" id={`brief-body-${playerKey}`}>
                  {!brief && (
                    <div className="player-briefs-drawer__row-empty">
                      No brief — tap Regenerate.
                    </div>
                  )}
                  {brief && !editing && (
                    <>
                      {SECTIONS.map((section) => {
                        const lines = brief[section.key];
                        if (!lines || lines.length === 0) return null;
                        return (
                          <div
                            key={section.key}
                            className="player-briefs-drawer__row-section"
                          >
                            <div className="player-briefs-drawer__row-section-label">
                              {section.label}
                            </div>
                            <ul className="player-briefs-drawer__row-bullets">
                              {lines.map((line, i) => (
                                <li key={i}>{line}</li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                      <div className="player-briefs-drawer__row-actions">
                        <button
                          type="button"
                          className="player-briefs-drawer__edit-button"
                          onClick={() => setEditingKey(playerKey)}
                          aria-label={`Edit brief for ${playerKey}`}
                        >
                          Edit
                        </button>
                        {(history[playerKey]?.length ?? 0) > 0 && (
                          <button
                            type="button"
                            className="player-briefs-drawer__edit-button"
                            onClick={() => setHistoryKey(historyKey === playerKey ? null : playerKey)}
                            aria-label={`Toggle history for ${playerKey}`}
                            aria-expanded={historyKey === playerKey}
                          >
                            History ({history[playerKey]!.length})
                          </button>
                        )}
                      </div>
                      {historyKey === playerKey && (history[playerKey]?.length ?? 0) > 0 && (
                        <div
                          className="player-briefs-drawer__history"
                          data-testid={`brief-history-${playerKey}`}
                        >
                          {history[playerKey]!.map((version, i) => (
                            <div key={i} className="player-briefs-drawer__history-row">
                              <div className="player-briefs-drawer__history-meta">
                                <span data-source={version.source}>{version.source}</span>
                                <span>{formatHistoryTimestamp(version.generatedAt)}</span>
                              </div>
                              <div className="player-briefs-drawer__history-preview">
                                {version.inPossession[0] ?? '—'}
                              </div>
                              {onRestore && (
                                <button
                                  type="button"
                                  className="player-briefs-drawer__edit-button"
                                  onClick={() => {
                                    onRestore(playerKey, version);
                                    setHistoryKey(null);
                                  }}
                                  aria-label={`Restore version ${i + 1} for ${playerKey}`}
                                >
                                  Restore
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {brief && editing && (
                    <BriefEditor
                      brief={brief}
                      onCancel={() => setEditingKey(null)}
                      onSave={(next) => {
                        onEdit(playerKey, next);
                        setEditingKey(null);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline editor — one <textarea> per section. Save flips source='manual'.
// ---------------------------------------------------------------------------

interface BriefEditorProps {
  brief: PlayerBrief;
  onSave: (next: PlayerBrief) => void;
  onCancel: () => void;
}

function BriefEditor({ brief, onSave, onCancel }: BriefEditorProps) {
  // Local copies keyed by section. Bullets are joined into a textarea
  // with one bullet per line so the coach edits naturally.
  const [draft, setDraft] = useState(() => ({
    inPossession: brief.inPossession.join('\n'),
    outOfPossession: brief.outOfPossession.join('\n'),
    transitions: brief.transitions.join('\n'),
    setPieces: brief.setPieces.join('\n'),
  }));

  const splitLines = (s: string): string[] =>
    s.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);

  const handleSave = () => {
    const next: PlayerBrief = {
      ...brief,
      inPossession: splitLines(draft.inPossession),
      outOfPossession: splitLines(draft.outOfPossession),
      transitions: splitLines(draft.transitions),
      setPieces: splitLines(draft.setPieces),
      source: 'manual',
      generatedAt: new Date().toISOString(),
    };
    onSave(next);
  };

  return (
    <div className="player-briefs-drawer__editor">
      {SECTIONS.map((section) => (
        <label key={section.key} className="player-briefs-drawer__editor-field">
          <span className="player-briefs-drawer__editor-label">{section.label}</span>
          <textarea
            className="player-briefs-drawer__editor-textarea"
            value={draft[section.key]}
            rows={3}
            onChange={(e) => setDraft((prev) => ({ ...prev, [section.key]: e.target.value }))}
            data-testid={`brief-editor-${section.key}`}
          />
        </label>
      ))}
      <div className="player-briefs-drawer__editor-actions">
        <button
          type="button"
          className="player-briefs-drawer__editor-cancel"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="player-briefs-drawer__editor-save"
          onClick={handleSave}
        >
          Save
        </button>
      </div>
    </div>
  );
}
